import { useMemo, useState } from "react";
import {
  Activity,
  ArrowDownLeft,
  ArrowLeftRight,
  ArrowUpRight,
  Award,
  CheckCircle2,
  Clock3,
  LayoutDashboard,
  Loader2,
  Search,
  Shield,
  XCircle,
} from "lucide-react";

import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useMyTrades } from "@/hooks";
import { type Trade, TradeStatus } from "@/interfaces/trade";
import { useAppSelector } from "@/store/hooks";
import TradeCard from "@/components/shared/TradeCard";

type TradeTab =
  | "all"
  | "active"
  | "pending"
  | "processing"
  | "completed"
  | "cancelled"
  | "expired";

type StatusFilter =
  | "all"
  | "PENDING"
  | "PAID"
  | "DISPUTED"
  | "UNDER_REVIEW"
  | "RELEASED"
  | "AUTO_RELEASED"
  | "REFUNDED"
  | "CANCELLED"
  | "EXPIRED";

const tabs = [
  {
    value: "all",
    label: "All Trades",
    icon: LayoutDashboard,
  },
  {
    value: "active",
    label: "Active",
    icon: ArrowLeftRight,
  },
  {
    value: "pending",
    label: "Pending",
    icon: Clock3,
  },
  {
    value: "processing",
    label: "Processing",
    icon: Activity,
  },
  {
    value: "completed",
    label: "Completed",
    icon: CheckCircle2,
  },
  {
    value: "cancelled",
    label: "Cancelled",
    icon: XCircle,
  },
  {
    value: "expired",
    label: "Expired",
    icon: Clock3,
  },
] as const;

const normalizeStatus = (status: TradeStatus | string): string =>
  String(status).trim().toUpperCase();

const isCompletedStatus = (status: string): boolean =>
  ["RELEASED", "AUTO_RELEASED"].includes(status);

const isCancelledStatus = (status: string): boolean =>
  ["CANCELLED", "REFUNDED"].includes(status);

const isExpiredStatus = (status: string): boolean => status === "EXPIRED";

const isProcessingStatus = (status: string): boolean =>
  ["PAID", "DISPUTED", "UNDER_REVIEW"].includes(status);

const matchesTab = (trade: Trade, tab: TradeTab): boolean => {
  const status = normalizeStatus(trade.status);

  switch (tab) {
    case "all":
      return true;

    case "active":
      return (
        status !== "PENDING" &&
        !isProcessingStatus(status) &&
        !isCompletedStatus(status) &&
        !isCancelledStatus(status) &&
        !isExpiredStatus(status)
      );

    case "pending":
      return status === "PENDING";

    case "processing":
      return isProcessingStatus(status);

    case "completed":
      return isCompletedStatus(status);

    case "cancelled":
      return isCancelledStatus(status);

    case "expired":
      return isExpiredStatus(status);

    default:
      return true;
  }
};

const UserTrades = () => {
  const { user } = useAppSelector((state) => state.auth);
  const userId = user?.id ?? "";

  const [activeTab, setActiveTab] = useState<TradeTab>("all");
  const [search, setSearch] = useState("");
  const [tradeFilter, setTradeFilter] = useState<StatusFilter>("all");

  const [selectedTrade, setSelectedTrade] = useState<Trade | null>(null);
  const [openReview, setOpenReview] = useState(false);

  const { data: myTrades, isLoading, isError, refetch } = useMyTrades(userId);

  const trades = useMemo<Trade[]>(() => myTrades ?? [], [myTrades]);

  const tabCounts = useMemo(() => {
    return {
      all: trades.length,
      active: trades.filter((trade) => matchesTab(trade, "active")).length,
      pending: trades.filter((trade) => matchesTab(trade, "pending")).length,
      processing: trades.filter((trade) => matchesTab(trade, "processing"))
        .length,
      completed: trades.filter((trade) => matchesTab(trade, "completed"))
        .length,
      cancelled: trades.filter((trade) => matchesTab(trade, "cancelled"))
        .length,
      expired: trades.filter((trade) => matchesTab(trade, "expired")).length,
    };
  }, [trades]);

  const filteredTrades = useMemo(() => {
    const query = search.trim().toLowerCase();

    return trades.filter((trade) => {
      const status = normalizeStatus(trade.status);

      const matchesSelectedTab = matchesTab(trade, activeTab);

      const matchesSearch =
        !query ||
        trade.id.toLowerCase().includes(query) ||
        (trade.buyer?.username ?? "").toLowerCase().includes(query) ||
        (trade.seller?.username ?? "").toLowerCase().includes(query) ||
        (trade.advertisement?.asset?.currency ?? "")
          .toLowerCase()
          .includes(query);

      const matchesStatusFilter =
        tradeFilter === "all" || status === tradeFilter;

      return matchesSelectedTab && matchesSearch && matchesStatusFilter;
    });
  }, [trades, activeTab, search, tradeFilter]);

  const handleTabChange = (value: string) => {
    if (tabs.some((tab) => tab.value === value)) {
      setActiveTab(value as TradeTab);
    }
  };

  return (
    <main className="min-h-screen w-full bg-background pb-12 pt-6">
      <div className="mx-auto w-full max-w-[1600px] px-4 sm:px-6 lg:px-8">
        {/* Page heading */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <div className="mb-2 flex items-center gap-2 text-sm text-muted-foreground">
              <ArrowLeftRight className="h-4 w-4 text-primary" />
              <span>Peer-to-peer marketplace</span>
            </div>

            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
              My Trades
            </h1>

            <p className="mt-1 text-sm text-muted-foreground">
              Track your purchases, sales, and trade activity.
            </p>
          </div>

          <div className="flex items-center gap-3 rounded-xl border bg-card px-4 py-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <ArrowLeftRight className="h-5 w-5 text-primary" />
            </div>

            <div>
              <p className="text-xs text-muted-foreground">Total trades</p>
              <p className="text-xl font-bold tabular-nums">{trades.length}</p>
            </div>
          </div>
        </div>

        {/* Sticky tab navigation */}
        <div className="sticky top-16 z-30 -mx-4 mb-6 border-b border-border/70 bg-background/95 px-4 pb-3 pt-3 backdrop-blur-xl sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
          <Tabs
            value={activeTab}
            onValueChange={handleTabChange}
            className="w-full min-w-0"
          >
            <div className="relative w-full min-w-0">
              <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-5 bg-gradient-to-r from-background to-transparent sm:hidden" />

              <div className="scrollbar-hide w-full overflow-x-auto">
                <TabsList className="flex h-auto w-max min-w-full items-center justify-start gap-2 rounded-none bg-transparent p-0">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.value;

                    return (
                      <TabsTrigger
                        key={tab.value}
                        value={tab.value}
                        className={[
                          "inline-flex h-11 shrink-0 items-center justify-center gap-2",
                          "rounded-full border px-5 text-sm font-medium",
                          "border-border bg-card text-muted-foreground",
                          "transition-all duration-200",
                          "hover:border-primary/40 hover:bg-muted hover:text-foreground",
                          "data-[state=active]:border-primary",
                          "data-[state=active]:bg-primary",
                          "data-[state=active]:text-primary-foreground",
                          "data-[state=active]:shadow-md",
                          "focus-visible:outline-none focus-visible:ring-2",
                          "focus-visible:ring-primary focus-visible:ring-offset-2",
                        ].join(" ")}
                        aria-current={isActive ? "page" : undefined}
                      >
                        <Icon className="h-4 w-4 shrink-0" />

                        <span>{tab.label}</span>

                        <span
                          className={[
                            "ml-1 inline-flex min-w-5 items-center justify-center rounded-md px-1.5 py-0.5 text-xs tabular-nums",
                            isActive
                              ? "bg-primary-foreground/15 text-primary-foreground"
                              : "bg-muted text-muted-foreground",
                          ].join(" ")}
                        >
                          {tabCounts[tab.value]}
                        </span>
                      </TabsTrigger>
                    );
                  })}
                </TabsList>
              </div>

              <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-5 bg-gradient-to-l from-background to-transparent sm:hidden" />
            </div>
          </Tabs>
        </div>

        {/* Selected tab content */}
        <section className="min-w-0">
          <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold tracking-tight">
                {tabs.find((tab) => tab.value === activeTab)?.label}
              </h2>

              <p className="mt-1 text-sm text-muted-foreground">
                {isLoading
                  ? "Loading your trades..."
                  : `${filteredTrades.length} ${
                      filteredTrades.length === 1 ? "trade" : "trades"
                    } found`}
              </p>
            </div>

            <div className="flex items-center gap-3 text-muted-foreground">
              <ArrowDownLeft className="h-5 w-5 text-emerald-500" />
              <ArrowUpRight className="h-5 w-5 text-red-500" />
              <span className="text-xs">Buy & sell activity</span>
            </div>
          </div>

          {/* Loading state */}
          {isLoading && (
            <div className="flex min-h-64 flex-col items-center justify-center gap-3 rounded-2xl border bg-card px-6 py-12 text-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="font-medium">Loading trades</p>
              <p className="text-sm text-muted-foreground">
                Fetching your latest trade activity.
              </p>
            </div>
          )}

          {/* Error state */}
          {!isLoading && isError && (
            <div className="flex min-h-64 flex-col items-center justify-center rounded-2xl border bg-card px-6 py-12 text-center">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
                <Shield className="h-6 w-6 text-destructive" />
              </div>

              <h3 className="font-semibold">Unable to load trades</h3>

              <p className="mt-2 max-w-md text-sm text-muted-foreground">
                Something went wrong while fetching your trades. Please try
                again.
              </p>

              <button
                type="button"
                onClick={() => void refetch()}
                className="mt-5 inline-flex h-10 items-center justify-center rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
              >
                Try again
              </button>
            </div>
          )}

          {/* Trade cards */}
          {!isLoading && !isError && filteredTrades.length > 0 && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {filteredTrades.map((trade) => (
                <TradeCard
                  key={trade.id}
                  trade={trade}
                  selectedTrade={selectedTrade}
                  setSelectedTrade={setSelectedTrade}
                  openReview={openReview}
                  setOpenReview={setOpenReview}
                />
              ))}
            </div>
          )}

          {/* Empty state */}
          {!isLoading && !isError && filteredTrades.length === 0 && (
            <div className="flex min-h-72 flex-col items-center justify-center rounded-2xl border border-dashed bg-card/50 px-6 py-12 text-center">
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
                {activeTab === "completed" ? (
                  <Award className="h-7 w-7 text-primary" />
                ) : activeTab === "cancelled" ? (
                  <XCircle className="h-7 w-7 text-muted-foreground" />
                ) : activeTab === "expired" ? (
                  <Clock3 className="h-7 w-7 text-muted-foreground" />
                ) : (
                  <Search className="h-7 w-7 text-muted-foreground" />
                )}
              </div>

              <h3 className="text-lg font-semibold">No trades found</h3>

              <p className="mt-2 max-w-md text-sm text-muted-foreground">
                {search || tradeFilter !== "all"
                  ? "No trades match your current search and filters. Try adjusting your search or status."
                  : activeTab === "all"
                    ? "Your trades will appear here when you start buying or selling."
                    : activeTab === "expired"
                      ? "You don't have any expired trades right now."
                      : `You don't have any ${activeTab} trades right now.`}
              </p>

              {(search || tradeFilter !== "all") && (
                <button
                  type="button"
                  onClick={() => {
                    setSearch("");
                    setTradeFilter("all");
                  }}
                  className="mt-5 inline-flex h-10 items-center justify-center rounded-lg border border-border bg-background px-4 text-sm font-medium transition-colors hover:bg-muted"
                >
                  Clear filters
                </button>
              )}
            </div>
          )}
        </section>
      </div>
    </main>
  );
};

export default UserTrades;
